import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
    BehaviorSubject,
    Observable,
    catchError,
    map,
    of,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { ApiResponse } from '../utils/api-response';
import { AuthResponse, StaffSchoolRoleDto, User } from './user.type';

@Injectable({ providedIn: 'root' })
export class UserSharedService {
    // Base URLs
    private readonly BASE_URL = environment.apiUrl;
    private readonly AUTHENTICATION =environment.authentication;
    private readonly CLIENT_ID = environment.clientId;
    private readonly USERS_URL = `${this.BASE_URL}/api/v1/users`;

    // BehaviorSubjects
    private _user: BehaviorSubject<User | null> =
        new BehaviorSubject<User | null>(null);
    private _users: BehaviorSubject<User[] | null> = new BehaviorSubject<
        User[] | null
    >(null);

    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set userCode(code: string) {
        localStorage.setItem('userCode', code);
    }

    get userCode(): string {
        return localStorage.getItem('userCode') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set userRole(code: string) {
        localStorage.setItem('userRole', code);
    }

    get userRole(): string {
        return localStorage.getItem('userRole') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set staffCode(code: string) {
        localStorage.setItem('staffCode', code);
    }

    get staffCode(): string {
        return localStorage.getItem('staffCode') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set selectedSchoolCode(code: string) {
        localStorage.setItem('selectedSchoolCode', code);
    }

    get selectedSchoolCode(): string {
        return localStorage.getItem('selectedSchoolCode') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    set selectedSchoolTimezone(timezone: string) {
        localStorage.setItem('selectedSchoolTimezone', timezone);
    }

    get selectedSchoolTimezone(): string {
        return localStorage.getItem('selectedSchoolTimezone') ?? '';
    }

    /**
     * Setter & getter for access token
     */
    // set accessToken(token: string) {
    //     localStorage.setItem('accessToken', token);
    // }

    // get accessToken(): string {
    //     return localStorage.getItem('accessToken') ?? '';
    // }

    // set refreshToken(token: string) {
    //     localStorage.setItem('refreshToken', token);
    // }

    // get refreshToken(): string {
    //     return localStorage.getItem('refreshToken') ?? '';
    // }

    /**
     * Authentication
     */
    set wowAccessToken(token: string) {
        localStorage.setItem('wow_access_token', token);
    }

    get wowAccessToken(): string {
        return localStorage.getItem('wow_access_token') ?? '';
    }

    set wowRefreshToken(token: string) {
        localStorage.setItem('wow_refresh_token', token);
    }

    get wowRefreshToken(): string {
        return localStorage.getItem('wow_refresh_token') ?? '';
    }

    // Accessors
    get user$(): Observable<User | null> {
        return this._user.asObservable();
    }

    get users$(): Observable<User[] | null> {
        return this._users.asObservable();
    }

    set permissionsBySchool(permissions: { [schoolCode: string]: string[] }) {
        localStorage.setItem('permissions', JSON.stringify(permissions));
    }

    get permissionsBySchool(): { [schoolCode: string]: string[] } {
        const permissions = localStorage.getItem('permissions');
        return permissions ? JSON.parse(permissions) : {};
    }

    // Helper to get permissions for a specific school
    getPermissionsForSchool(schoolCode: string): string[] {
        return this.permissionsBySchool[schoolCode] || [];
    }

    /**
     * Checks if the user has the specified permission for the given school.
     * @param schoolCode The school code to check permissions for.
     * @param requestedPermission The permission name to check (e.g., "CLASSROOM_CREATE").
     * @returns true if the permission exists for the school, false otherwise.
     */
    hasSchoolPermission(requestedPermission: string): boolean {
        const schoolCode = localStorage.getItem('selectedSchoolCode') || '';
        if (!schoolCode) {
            console.warn(
                'No school code found in localStorage. Cannot check permissions.'
            );
            return false;
        }
        const permissions = this.getPermissionsForSchool(schoolCode);
        return permissions.includes(requestedPermission);
    }

    hasAnySchoolPermission(requestedPermissions: string[]): boolean {
        const schoolCode = localStorage.getItem('selectedSchoolCode') || '';
        if (!schoolCode) {
            console.warn(
                'No school code found in localStorage. Cannot check permissions.'
            );
            return false;
        }
        const permissions = this.getPermissionsForSchool(schoolCode);
        return requestedPermissions.some((p) => permissions.includes(p));
    }

    /**
     * Setter & getter for staffSchoolRoles by school code.
     * Stores an object: { [schoolCode: string]: StaffRole[] }
     */
    set staffSchoolRolesBySchool(rolesBySchool: {
        [schoolCode: string]: any[];
    }) {
        localStorage.setItem('staffSchoolRoles', JSON.stringify(rolesBySchool));
    }

    get staffSchoolRolesBySchool(): { [schoolCode: string]: any[] } {
        const data = localStorage.getItem('staffSchoolRoles');
        return data ? JSON.parse(data) : {};
    }

    /**
     * Set staffSchoolRoles array for a specific school code.
     */
    setStaffSchoolRolesForSchool(schoolCode: string, roles: any[]): void {
        const allRoles = this.staffSchoolRolesBySchool;
        allRoles[schoolCode] = roles;
        this.staffSchoolRolesBySchool = allRoles;
    }

    /**
     * Get staffSchoolRoles array for a specific school code.
     */
    getStaffSchoolRolesForSchool(schoolCode: string): any[] {
        const allRoles = this.staffSchoolRolesBySchool;
        return allRoles[schoolCode] || [];
    }

    // Public Methods

    /**
     * Get all users
     */
    getUsers(): Observable<ApiResponse<User[]>> {
        return this._httpClient
            .get<ApiResponse<User[]>>(`${this.USERS_URL}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._users.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch users:', error);
                    const errorResponse: ApiResponse<User[]> = error?.error || {
                        success: false,
                        message: error.message || 'Failed to fetch users',
                        data: null,
                        timestamp: new Date(),
                    };
                    return throwError(() => errorResponse);
                })
            );
    }

    /**
     * Get user by code
     */
    getUserByCode(code: string): Observable<ApiResponse<User>> {
        return this._httpClient
            .get<ApiResponse<User>>(`${this.USERS_URL}/${code}`)
            .pipe(
                tap((response) => {
                    if (response.success && response.data) {
                        this._user.next(response.data);
                    }
                }),
                catchError((error) => {
                    console.error('Failed to fetch user:', error);
                    const errorResponse: ApiResponse<User> = error?.error || {
                        success: false,
                        message: error.message || 'Failed to fetch user',
                        data: null,
                        timestamp: new Date(),
                    };
                    return throwError(() => errorResponse);
                })
            );
    }
    /********************
     * Authentication (get Bearer token)
     ***********/
    getWowAccessToken(username: string, password: string): Observable<string> {
        const url = this.AUTHENTICATION;
        const body = new URLSearchParams({
            grant_type: 'password',
            client_id: this.CLIENT_ID, 
            username,
            password
        }).toString();

        return this._httpClient.post<any>(url, body, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).pipe(
            tap(response => {
                if (response.access_token) {
                    localStorage.setItem('wow_access_token', response.access_token);
                }
                if (response.refresh_token) {
                    localStorage.setItem('wow_refresh_token', response.refresh_token);
                }
            }),
            map(response => response.access_token ?? ''),
            catchError(error => {
                console.error('Failed to fetch wow access token:', error);
                return throwError(() => error);
            })
        );
    }
    /**
     * Refresh access token using refresh token
     */
    refreshWowAccessToken(): Observable<string> {
        const refreshToken = this.wowRefreshToken;
        if (!refreshToken) {
            return throwError(() => new Error('No refresh token available'));
        }

        const url = this.AUTHENTICATION;
        const body = new URLSearchParams({
            grant_type: 'refresh_token',
            client_id: this.CLIENT_ID,
            refresh_token: refreshToken
        }).toString();

        return this._httpClient.post<any>(url, body, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded'
            }
        }).pipe(
            tap(response => {
                if (response.access_token) {
                    localStorage.setItem('wow_access_token', response.access_token);
                }
                if (response.refresh_token) {
                    localStorage.setItem('wow_refresh_token', response.refresh_token);
                }
            }),
            map(response => response.access_token ?? ''),
            catchError(error => {
                console.error('Failed to refresh token:', error);
                // Clear tokens if refresh fails
                this.clearTokens();
                return throwError(() => error);
            })
        );
    }
    /**
     * Clear all tokens
     */
    clearTokens(): void {
        localStorage.removeItem('wow_access_token');
        localStorage.removeItem('wow_refresh_token');
        localStorage.removeItem('userCode');
        localStorage.removeItem('userRole');
        localStorage.removeItem('permissions');
        localStorage.removeItem('staffSchoolRoles');
        localStorage.removeItem('accessToken');
        localStorage.removeItem('staffCode');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('selectedSchoolTimezone');
        localStorage.removeItem('selectedSchoolCode');
        // localStorage.removeItem('userRole');
        // localStorage.removeItem('userRole');
    }

    authenticate(username: string, password: string): Observable<User> {
        const formData = new FormData();
        formData.append('username', username);
        formData.append('password', password);

        return this._httpClient
            .post<ApiResponse<AuthResponse>>(
                `${this.USERS_URL}/authenticate`,
                formData
            )
            .pipe(
                map((response) => {
                    if (response.success && response.data) {
                        const authData = response.data;
                        this._user.next(authData.user);
                        // this.accessToken = authData.accessToken;
                        // this.refreshToken = authData.refreshToken;
                        this.userCode = authData.user.code;

                        const roles = authData.roles || {};
                        const permissionsBySchool: {
                            [schoolCode: string]: string[];
                        } = {};

                        Object.keys(roles).forEach((schoolCode) => {
                            permissionsBySchool[schoolCode] = (
                                roles[schoolCode].permissions || []
                            ).map((perm: any) => perm.name);
                        });

                        this.permissionsBySchool = permissionsBySchool;

                        const staffSchoolRoles =
                            response.data.user.staffSchoolRoles;
                        if (Array.isArray(staffSchoolRoles)) {
                            const rolesBySchool: {
                                [schoolCode: string]: StaffSchoolRoleDto[];
                            } = {};
                            staffSchoolRoles.forEach((role) => {
                                if (role.schoolCode) {
                                    if (!rolesBySchool[role.schoolCode]) {
                                        rolesBySchool[role.schoolCode] = [];
                                    }
                                    rolesBySchool[role.schoolCode].push(role);
                                }
                            });
                            Object.keys(rolesBySchool).forEach((schoolCode) => {
                                this.setStaffSchoolRolesForSchool(
                                    schoolCode,
                                    rolesBySchool[schoolCode]
                                );
                            });
                        }

                        return response.data.user;
                    } else {
                        throw new Error(
                            response.message || 'Authentication failed'
                        );
                    }
                }),
                catchError((error) => {
                    console.error('Authentication failed:', error);
                    throw error;
                })
            );
    }

    /**
     * Send OTP to user email
     */
    sendOtp(email: string): Observable<ApiResponse<null>> {
        const url = `${this.USERS_URL}/forget-send-otp?email=${encodeURIComponent(email)}`;
      
        return this._httpClient
          .post<ApiResponse<null>>(url, {})
          .pipe(
            tap((response) => {
              if (response.success) {
                // console.log('OTP sent successfully:', response.message);
              }
            }),
            catchError((error) => {
              console.error('Failed to send OTP:', error);
              const errorResponse: ApiResponse<null> = error?.error || {
                success: false,
                message: error.message || 'Failed to send OTP',
                data: null,
                timestamp: new Date().toISOString(),
              };
              return throwError(() => errorResponse);
            })
          );
      }

    /**
     * Reset user password
     * 
     */

    verifyOtpAndResetPassword(email: string, otp: string, newPassword: string): Observable<ApiResponse<null>> {
        const url = `${this.USERS_URL}/forgot-password`;
        const params = new HttpParams()
          .set('email', email)
          .set('otp', otp)
          .set('newPassword', newPassword);
      
        return this._httpClient.post<ApiResponse<null>>(url, {}, { params }).pipe(
          tap((response) => {
            // console.log('Password reset response:', response.message);
          }),
          catchError((error) => {
            console.error('Reset password failed:', error);
            const errorResponse: ApiResponse<null> = error?.error || {
              success: false,
              message: error.message || 'Password reset failed',
              data: null,
              timestamp: new Date().toISOString(),
            };
            return throwError(() => errorResponse);
          })
        );
      }

    /**
     * Get user counts (example: total users, users with admin role, etc.)
     */
    getUserCounts(): Observable<
        ApiResponse<{ total: number; admins: number; staffs: number }>
    > {
        return this.users$.pipe(
            take(1),
            map((users) => {
                const counts = {
                    total: users?.length || 0,
                    admins: 0, //users?.filter(u => u.staffSchoolRoles.roles?.includes('ADMIN')).length || 0,
                    staffs: 0, //users?.filter(u => u.roles?.includes('STAFF')).length || 0
                };
                return {
                    success: true,
                    data: counts,
                    message: 'User counts retrieved successfully',
                    timestamp: new Date(),
                };
            })
        );
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        const usercCode = localStorage.getItem('userCode');
        if (usercCode) {
            return of(true);
        }

        // If the user is not logged in, return false
        return of(false);
    }

    /**
     * Change user password
     */
    changePassword(email: string, 
        // currentPassword: string,
        
        newPassword: string): Observable<ApiResponse<null>> {
        const url = `${this.USERS_URL}/change-password`;
        const params = new HttpParams()
            .set('email', email)
            // .set('currentPassword', currentPassword)
            .set('newPassword', newPassword);

        return this._httpClient.post<ApiResponse<null>>(url, {}, { params }).pipe(
            catchError((error) => {
                const errorResponse: ApiResponse<null> = error?.error || {
                    success: false,
                    message: error.message || 'Failed to change password',
                    data: null,
                    timestamp: new Date().toISOString(),
                };
                return throwError(() => errorResponse);
            })
        );
    }

    getFirebaseToken(): Observable<string> {
        return this._httpClient
            .get<ApiResponse<string>>(`${this.BASE_URL}/firebase/token`)
            .pipe(map((res) => res.data));
    }
}
